import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './NavigationMenu.scss';
import PerfectScrollbar from 'react-perfect-scrollbar';
import classNames from 'classnames';
import { useGetActiveMenuQuery } from '../../store/menu/menuApi';

export interface INavigationMenuProps {
	onCategoryChange: (categoryId: string) => void;
}

type SelectedData = { offsetLeft: number; width: number } | null;

export const NavigationMenu = ({ onCategoryChange }: INavigationMenuProps): JSX.Element => {
	const { data: menu } = useGetActiveMenuQuery();
	const { categories = [] } = menu ?? { categories: [] };

	// Default active index: if many categories, preselect index 3, else 0.
	const [activeIndex, setActiveIndex] = useState<number>(categories.length > 4 ? 3 : 0);

	// Recompute default when categories arrive/change:
	useEffect(() => {
		setActiveIndex(categories.length > 4 ? 3 : 0);
	}, [categories.length]);

	const [selectedItemData, setSelectedItemData] = useState<SelectedData>(null);
	const [noAnimate, setNoAnimate] = useState(false); // true during resize → no CSS transition

	const navigationMenuRef = useRef<HTMLDivElement | null>(null);
	const rafRef = useRef<number | null>(null);
	const roRef = useRef<ResizeObserver | null>(null);
	const resizeTimer = useRef<number | null>(null);

	const recalc = useCallback(() => {
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
		}

		rafRef.current = requestAnimationFrame(() => {
			const container = navigationMenuRef.current;

			if (!container) {
				return;
			}

			const item = container.getElementsByClassName('navigationMenuItem')[activeIndex] as HTMLElement | undefined;

			if (!item) {
				return;
			}

			setSelectedItemData({ offsetLeft: item.offsetLeft, width: item.offsetWidth });
		});
	}, [activeIndex]);

	// Measure after layout changes (categories/activeIndex)
	useLayoutEffect(() => {
		recalc();

		return () => {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
		};
	}, [recalc, categories]);

	// Window resize & orientationchange: disable animation briefly, recalc, then re-enable.
	useEffect(() => {
		const onResize = (): void => {
			if (!noAnimate) {
				setNoAnimate(true);
			}

			recalc();

			if (resizeTimer.current) {
				window.clearTimeout(resizeTimer.current);
			}

			resizeTimer.current = window.setTimeout(() => {
				setNoAnimate(false);
			}, 150);
		};

		window.addEventListener('resize', onResize);
		window.addEventListener('orientationchange', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
			window.removeEventListener('orientationchange', onResize);

			if (resizeTimer.current) {
				window.clearTimeout(resizeTimer.current);
			}
		};
	}, [recalc, noAnimate]);

	// Observe container size changes (parent layout shifts, scrollbar show/hide, etc.)
	useEffect(() => {
		const el = navigationMenuRef.current;

		if (!el || typeof ResizeObserver === 'undefined') {
			return;
		}

		roRef.current = new ResizeObserver(() => recalc());
		roRef.current.observe(el);

		return () => {
			roRef.current?.disconnect();
			roRef.current = null;
		};
	}, [recalc]);

	const handleItemClick = (index: number, id: string): void => {
		setNoAnimate(false); // ensure animation is allowed for user action
		setActiveIndex(index);
		onCategoryChange(id);
	};

	const centerLeft = selectedItemData ? selectedItemData.offsetLeft + selectedItemData.width / 2 : undefined;

	return (
		<div className="navigationMenuScrollerContainer">
			<PerfectScrollbar className="navigationMenuScroller">
				{categories.length > 0 && (
					<div className="navigationMenuContainer" ref={navigationMenuRef}>
						{categories.map((category, index) => (
							<div
								key={category.id}
								className={classNames('navigationMenuItem', { active: index === activeIndex })}
								onClick={(): void => handleItemClick(index, category.id)}
							>
								{category.title}
							</div>
						))}

						{selectedItemData && (
							<div className={classNames('indicator', { 'no-animate': noAnimate })} style={{ left: `${centerLeft}px` }} aria-hidden>
								<img src="/images/indicator.png" alt="" />
							</div>
						)}
					</div>
				)}
			</PerfectScrollbar>
		</div>
	);
};

export default NavigationMenu;
